import { FC } from 'react';
import { createPortal } from 'react-dom';
import Modal from '@/components/Modal/modal';
import buttonStyles from '@/components/Button/button.module.scss';
import useSession from '@/hooks/useSession';
import { ResponseSource } from '@/models/thread';
import { classnames } from '@/utils/functions';
import {
  getSourceHost,
  getSourceType,
  isQuantitativeSource,
  SourceType,
} from '@/pages/Thread/utils/sources';
import SourceIndicator from '../../SourceIndicator';
import ExternalLinkIcon from '@/assets/icons/arrow-up-right-from-square.svg?react';
import styles from './source-details-modal.module.scss';

interface SourceDetailsModalProps {
  source: ResponseSource;
  /* 1-based number shown on the source's indicators */
  index: number;
  /* The statement the clicked marker supports; omitted when opened from the source list */
  statement?: string;
  /* How many inline markers in the answer point to this source */
  citedCount?: number;
  onClose: () => void;
}

const TYPE_LABELS: Record<SourceType, string> = {
  table: 'Dataset',
  pdf: 'PDF',
  article: 'Article',
};

const SampleData: FC<{ source: ResponseSource }> = ({ source }) => {
  const { url, sample, columns, provider, row_count } = source;
  const rowCount = Number(row_count);
  /* Name HDX only on links that actually go there */
  const linkLabel =
    getSourceHost(url) === 'data.humdata.org'
      ? 'View dataset on HDX'
      : 'View dataset';

  if (!Array.isArray(sample) || sample.length === 0) {
    return <div>No sample data available.</div>;
  }

  if (!Array.isArray(columns) || columns.length === 0) {
    // Fallback: show JSON if columns are missing
    return <pre>{JSON.stringify(sample, null, 2)}</pre>;
  }

  return (
    <section className={styles['sample']}>
      {(provider || url) && (
        <p className={styles['provenance']}>
          {provider && (
            <span>
              Published by <strong>{provider}</strong>
            </span>
          )}
          {provider && url && <span aria-hidden="true">·</span>}
          {url && (
            <a
              href={url}
              className={styles['source-link']}
              target="_blank"
              rel="noopener noreferrer"
              title={url}
            >
              {linkLabel}
              <ExternalLinkIcon aria-hidden="true" />
            </a>
          )}
        </p>
      )}
      <div className={styles['sample-table-container']}>
        <table className={styles['sample-table']}>
          <thead>
            <tr>
              {columns.map((col: string) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sample.map((row: Record<string, unknown>, idx: number) => (
              <tr key={idx}>
                {columns.map((col: string) => (
                  <td key={col}>{String(row[col] ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rowCount > sample.length && (
        <p className={styles['row-count']}>
          Showing {sample.length} of {rowCount.toLocaleString()} rows
        </p>
      )}
    </section>
  );
};

const SourceDetailsModal: FC<SourceDetailsModalProps> = ({
  source,
  index,
  statement,
  citedCount,
  onClose,
}) => {
  const { pushUserEvent } = useSession();
  const type = getSourceType(source);
  const origin = source.provider || getSourceHost(source.url);
  const isQuantitative = isQuantitativeSource(source);

  return createPortal(
    <Modal
      title={source.title}
      isOpen={true}
      onClose={onClose}
      size={isQuantitative ? 'large' : 'medium'}
    >
      <div className={styles['source-details']}>
        <div className={styles['meta']}>
          <SourceIndicator id={index} />
          <span>
            {TYPE_LABELS[type]}
            {origin && ` · ${origin}`}
          </span>
        </div>

        {statement && (
          <section className={styles['cited-here']}>
            <h3>Cited here</h3>
            <blockquote>{statement}</blockquote>
            {citedCount !== undefined && citedCount > 1 && (
              <p>Cited {citedCount} times in this answer</p>
            )}
          </section>
        )}

        {isQuantitative ? (
          <SampleData source={source} />
        ) : (
          source.url && (
            <a
              className={classnames(
                buttonStyles['button'],
                styles['open-source']
              )}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => pushUserEvent('click_response_source')}
            >
              Open {type === 'pdf' ? 'PDF' : 'article'}
              <ExternalLinkIcon aria-hidden="true" />
            </a>
          )
        )}
      </div>
    </Modal>,
    document.body
  );
};

export default SourceDetailsModal;
